/**
 * panecount.ts — how many chart panes are shown and which ones, as pure functions.
 *
 * WHY (2026-10-19, intent 261019-pane-layout, FR-2). Andrei asked for a TradingView-style picker
 * of 1 to 4 panes. Until now the grid was always 1W / 1D / 15m (`PANE_SPEC` in `main.ts`), and at
 * 390 px the phone stacked all three, leaving the 15m execution pane 3/5 of the chart area.
 *
 * WHY A MODULE. `main.ts` calls `createChart` and `localStorage` at import time, so vitest cannot
 * import it (see `inspector.ts` for the same reason). Which panes exist for an N, what a stored
 * value means and what an absent or junk value means are exactly the parts a test must pin, so
 * they live here. The DOM build and teardown stay in `main.ts`.
 *
 * ONE TABLE, NO FORK. `PANE_SPEC` holds all four specs in top-down order (1W -> 1D -> 4h -> 15m)
 * and `panesFor(n)` picks rows from it, so the 4th pane is built by the same code as the other
 * three (FR-2.3). Its default TF is 4h (Requirements Q3 A): the order then reads top-down, as in
 * the owner's screenshot.
 *
 * `pane-ltf` IS IN EVERY ROW. `activePane()` in `main.ts` throws without it, and the order ticket,
 * drawings and live poll all hang off it.
 */

/** Every pane the app can show, top-down. Ids are stable: they key the saved per-pane state. */
export const PANE_SPEC = [
  { id: 'pane-htf', tf: '1W' },
  { id: 'pane-mtf', tf: '1D' },
  { id: 'pane-4th', tf: '4h' },
  { id: 'pane-ltf', tf: '15m' },
] as const;

export type PaneSpec = (typeof PANE_SPEC)[number];
export type PaneId = PaneSpec['id'];

/** The execution pane: orders, drawings and the ticket attach here. Never torn down. */
export const EXECUTION_PANE_ID = 'pane-ltf';

export const MIN_PANE_COUNT = 1;
export const MAX_PANE_COUNT = 4;
/** Today's layout (1W, 1D, 15m). A first visit, or junk in storage, gets this. */
export const DEFAULT_PANE_COUNT = 3;

/** localStorage key for the chosen N. Versioned like `panes.v2` so a new encoding gets a new key. */
export const PANE_COUNT_KEY = 'panes.count.v1';

/** The FR-2.2 table: which pane ids N shows. */
const TABLE: Record<number, readonly PaneId[]> = {
  1: ['pane-ltf'],
  2: ['pane-htf', 'pane-ltf'],
  3: ['pane-htf', 'pane-mtf', 'pane-ltf'],
  4: ['pane-htf', 'pane-mtf', 'pane-4th', 'pane-ltf'],
};

/** The pane specs for N, in `PANE_SPEC` (top-down) order. An N outside 1..4 is the default's. */
export function panesFor(n: number): PaneSpec[] {
  const ids = TABLE[n] ?? TABLE[DEFAULT_PANE_COUNT];
  return PANE_SPEC.filter((s) => ids.includes(s.id));
}

/**
 * What a stored value means. Only the exact strings '1'..'4' are an N; a missing key, '0', '5',
 * '2.5', ' 2' or 'abc' all mean the default, so a junk value never yields zero panes or a fifth.
 */
export function parsePaneCount(raw: string | null | undefined): number {
  if (typeof raw !== 'string' || !/^[1-4]$/.test(raw)) return DEFAULT_PANE_COUNT;
  return Number(raw);
}

/** Minimal storage shape, so a test can pass a Map-backed fake. */
export interface PaneCountStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The saved N, or the default when storage is missing, throws (private mode) or holds junk. */
export function loadPaneCount(storage: PaneCountStorage | null | undefined): number {
  try {
    return parsePaneCount(storage?.getItem(PANE_COUNT_KEY));
  } catch {
    return DEFAULT_PANE_COUNT; // storage disabled: the layout still renders, it just is not remembered
  }
}

/** Save N. Returns false when it could not be saved; a value outside 1..4 is refused, not clamped. */
export function savePaneCount(storage: PaneCountStorage | null | undefined, n: number): boolean {
  if (!Number.isInteger(n) || n < MIN_PANE_COUNT || n > MAX_PANE_COUNT || !storage) return false;
  try {
    storage.setItem(PANE_COUNT_KEY, String(n));
    return true;
  } catch {
    return false; // quota or private mode: not remembered, but the live layout already changed
  }
}

/**
 * The N actually built. A phone (`MOBILE_QUERY` matches) always gets 1, the execution pane at full
 * height (FR-3.1). This only reads the saved desktop N; it never rewrites it, so turning the phone
 * to a wider window restores the desktop layout.
 */
export function effectivePaneCount(saved: number, isPhone: boolean): number {
  return isPhone ? 1 : parsePaneCount(String(saved));
}

/** The grid modifier class on `#chart` for N (`pane-n1` .. `pane-n4`, CSS in `index.html`). */
export function gridClass(n: number): string {
  return `pane-n${parsePaneCount(String(n))}`;
}
